
import { ActionCreator } from 'app/redux/ActionCreator'
import { Profile } from 'app/redux/Profile/model'
import { ActionTypes, IProfileAction, IProfileState } from 'app/redux/Profile/reducer'
import { AccountsService } from 'app/services/AccountsService'
import { FamilyInfoResponse, FamilyInfoService } from 'app/services/FamilyInfoService'
import { OperationStatus } from 'app/services/OperationStatus'
import { genericFailure } from 'app/services/WebServiceBase'
import { disableProfileStore, getWebServicesConfig, resetProfile } from 'config'
import * as _ from 'lodash'
import * as moment from 'moment'

class ProfileActionCreator extends ActionCreator<IProfileState, IProfileAction> {

  FamilyInfoSvc = new FamilyInfoService(getWebServicesConfig().familyInfoEndpoint)
  lastProfileSaved: Profile = null

  /**
   * Get profile
   * @param accessToken
   * @param service
   */
  retrieve(extraInfo: any): Promise<Profile> {
    const accessToken = this.getSharedVar('accessToken')

    if (!accessToken) {
      return Promise.reject('Could not retrieve profile: No Access Token found')
    }

    this.dispatch(this.retrieveStarting())

    return this.FamilyInfoSvc.getFamilyInfo(accessToken)
      .then((response) => {
        if (response.IsError) {
          this.dispatch(this.retrieveFailed(response))
        } else {
          this.dispatch(this.retrieveCompleted(response))
        }
        return response.info
      })
      .then((profile) => {
        let firstTime = false
        // if there is no profile, make one now
        if (!profile) {
          firstTime = true
          profile = new Profile()
          profile.createdWhen = moment.utc()
        }

        profile.mergeExtraInfo(extraInfo)

        if (resetProfile) {
          // tslint:disable-next-line:no-console
          console.log('Resetting user profile.')
          profile.pension = undefined
        }

        // since we just logged in, we need to update that
        profile.lastLoginWhen = moment.utc()
        return ProfileActions.store(profile, firstTime)
      })
      .catch((reason) => {
        // tslint:disable-next-line:no-console
        console.log('getFamilyInfo exception. ex: ' + reason)
        this.dispatch(this.retrieveFailed(reason))
        return reason
      })
  }

  /**
   * Store profile
   * @param accessToken
   * @param profile
   * @param firstTime
   * @param service
   */
  store(profile: Profile, firstTime?: boolean): Promise<Profile> {
    const accessToken = this.getSharedVar('accessToken')
    const storeNotRequired = disableProfileStore || _.isEqual(this.lastProfileSaved, profile)

    if (!accessToken) {
      return Promise.reject('Could not store profile: No Access Token found')
    }

    this.dispatch(this.storeStarting(profile))

    if (storeNotRequired) {
      this.dispatch(this.storeCompleted())
      return Promise.resolve(profile)
    }

    profile.modifiedWhen = moment.utc()
    profile.cleanUpAutoExpenses()

    return this.FamilyInfoSvc.storeFamilyInfo(accessToken, profile, firstTime)
      .then((response) => {
        if (response.IsError) {
          this.dispatch(this.storeFailed(response))
        } else {
          this.dispatch(this.storeCompleted())
        }

        this.lastProfileSaved = _.cloneDeep(profile)

        return profile
      })
      .catch((reason) => {
        this.dispatch(this.storeFailed(reason))
        return reason
      })
  }

  private storeCompleted(): IProfileAction {
    return {
      type: ActionTypes.PROFILE_STORE_COMPLETED,
      profile: { op: new OperationStatus() }
    }
  }

  private storeFailed(reason: any): IProfileAction {
    return {
      type: ActionTypes.PROFILE_STORE_FAILED,
      profile: { op: genericFailure(reason) }
    }
  }

  private storeStarting(info: Profile): IProfileAction {
    const op = new OperationStatus().setStarting('store familyInfo starting')
    return {
      type: ActionTypes.PROFILE_STORE_STARTING,
      profile: { op, info }
    }
  }

  private retrieveCompleted(response: FamilyInfoResponse): IProfileAction {
    return {
      type: ActionTypes.PROFILE_RETRIEVAL_COMPLETED,
      profile: { info: response.info, op: new OperationStatus().update(response) }
    }
  }

  private retrieveFailed(reason: any): IProfileAction {
    return {
      type: ActionTypes.PROFILE_RETRIEVAL_FAILED,
      profile: { op: genericFailure(ActionTypes.PROFILE_RETRIEVAL_FAILED) }
    }
  }

  private retrieveStarting(): IProfileAction {
    return {
      type: ActionTypes.PROFILE_RETRIEVAL_STARTING,
      profile: { op: new OperationStatus().setStarting('retrieve familyInfo starting') }
    }
  }

}

const ProfileActions = new ProfileActionCreator()

export {
  ProfileActions,
  ProfileActionCreator
}



// WEBPACK FOOTER //
// ./src/app/redux/Profile/actions/index.ts